import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2Icon,
  IdCardIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { cn } from '@/shared/utils/cn';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiCargoIdDelete, useCargoIds } from '@/entities/cargo-id';
import type { ApiCargoId } from '@/shared/types/api/cargo-ids';

const INVALIDATE = [queryKeys.cargoIds.all, queryKeys.parcels.all];

type StateBadgeProps = {
  status: ApiCargoId['status'];
  label: string;
};

const StateBadge = ({ status, label }: StateBadgeProps) => {
  const styles =
    status === 'APPROVED'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : status === 'PENDING'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-red-200 bg-red-50 text-red-700';
  const Icon =
    status === 'APPROVED'
      ? CheckCircle2Icon
      : status === 'PENDING'
        ? Loader2Icon
        : TriangleAlertIcon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold',
        styles,
      )}
    >
      <Icon
        className={cn('h-3.5 w-3.5', status === 'PENDING' && 'animate-spin')}
        aria-hidden
      />
      {label}
    </span>
  );
};

export const WidgetsIdsMain = () => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useCargoIds();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const remove = useMutate({
    api: (id: string) => apiCargoIdDelete(id),
    invalidateKeys: INVALIDATE,
    onSuccess: () => {
      toast.success(t('toast.deleted'));
      setDeleteOpen(false);
    },
    onError: (_e, message) => toast.error(message),
  });

  return (
    <>
      <PageHeader title={t('title')} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState
            message={extractErrorMessage(error, t('loadError'))}
            onRetry={() => void refetch()}
          />
        ) : !data ? (
          <EmptyState
            icon={<IdCardIcon className="h-10 w-10" aria-hidden />}
            title={t('emptyTitle')}
            description={t('emptyDescription')}
            action={
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                <Button
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                  onClick={() => navigate('/ids/request')}
                >
                  {t('requestAction')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/ids/connect')}
                >
                  {t('connectAction')}
                </Button>
              </div>
            }
          />
        ) : data.status === 'APPROVED' ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-brand-300 bg-gradient-to-br from-brand-50 to-white p-5 shadow-card ring-1 ring-brand-200">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                {t('profileBanner.label')}
              </p>
              <StateBadge status="APPROVED" label={t('profileBanner.trustChip')} />
            </div>
            <p className="break-all font-mono text-2xl font-bold tracking-wide text-brand-800">
              {data.code ?? '—'}
            </p>
            <div className="flex flex-col gap-1 text-sm text-ink">
              <p className="font-medium">{data.full_name}</p>
              {data.phone_number && (
                <p className="text-xs text-ink-muted">{data.phone_number}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-medium text-ink-soft">
                  {t(`entityType.${data.entity_type}`)}
                </span>
              </div>
            </div>
            <p className="text-xs text-ink-muted">{t('profileBanner.helper')}</p>
            <div className="flex flex-wrap gap-2 border-t border-brand-200 pt-3">
              <Button
                size="sm"
                variant="danger"
                leftIcon={<Trash2Icon className="h-4 w-4" />}
                disabled={remove.isPending}
                onClick={() => setDeleteOpen(true)}
              >
                {t('actions.unbind')}
              </Button>
            </div>
          </div>
        ) : data.status === 'PENDING' ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-card">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                {t('profileBanner.label')}
              </p>
              <StateBadge status="PENDING" label={t('profileBanner.pendingTitle')} />
            </div>
            <div className="flex flex-col gap-1 text-sm text-ink">
              <p className="font-medium">{data.full_name}</p>
              {data.phone_number && (
                <p className="text-xs text-ink-muted">{data.phone_number}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 font-medium text-ink-soft">
                  {t(`entityType.${data.entity_type}`)}
                </span>
              </div>
            </div>
            <p className="text-xs text-ink-muted">{t('profileBanner.pendingHelper')}</p>
            <div className="flex flex-wrap gap-2 border-t border-amber-200 pt-3">
              <Button
                size="sm"
                variant="danger"
                leftIcon={<Trash2Icon className="h-4 w-4" />}
                disabled={remove.isPending}
                onClick={() => setDeleteOpen(true)}
              >
                {t('actions.delete')}
              </Button>
            </div>
          </div>
        ) : (
          // REJECTED
          <div className="flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 shadow-card">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                {t('profileBanner.label')}
              </p>
              <StateBadge status="REJECTED" label={t('rejectedTitle')} />
            </div>
            <div className="flex flex-col gap-1 text-sm text-ink">
              <p className="font-medium">{data.full_name}</p>
              {data.phone_number && (
                <p className="text-xs text-ink-muted">{data.phone_number}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 font-medium text-ink-soft">
                  {t(`entityType.${data.entity_type}`)}
                </span>
              </div>
            </div>
            {data.reject_reason && (
              <div className="rounded-lg border border-red-200 bg-white p-3 text-sm text-ink">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                  {t('rejectReason')}
                </p>
                <p className="mt-1">{data.reject_reason}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 border-t border-red-200 pt-3">
              <Button
                size="sm"
                leftIcon={<PlusIcon className="h-4 w-4" />}
                onClick={() => navigate('/ids/request')}
              >
                {t('requestAgain')}
              </Button>
              <Button
                size="sm"
                variant="danger"
                leftIcon={<Trash2Icon className="h-4 w-4" />}
                disabled={remove.isPending}
                onClick={() => setDeleteOpen(true)}
              >
                {t('actions.delete')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={
          data?.status === 'APPROVED'
            ? t('confirmUnbindTitle')
            : t('confirmDeleteTitle')
        }
        description={
          data?.status === 'APPROVED'
            ? t('confirmUnbindDescription')
            : data?.status === 'REJECTED'
              ? t('confirmDeleteRejected')
              : t('confirmDeletePending')
        }
        confirmLabel={
          data?.status === 'APPROVED'
            ? t('actions.unbind')
            : t('confirmDeleteAction')
        }
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => {
          if (!remove.isPending) setDeleteOpen(false);
        }}
        onConfirm={() => data && remove.mutate(data.id)}
      />
    </>
  );
};
